import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  DatePicker,
  Input,
  PermissionsProvider,
  SearchableDropdown,
  Select,
} from '@/components';
import {Filter} from '@/components/Filter';
import type {BusinessInvoiceListParams} from '@/types';
import {BusinessInvoiceStatus} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';

import InvoiceCreateButton from './InvoiceCreateButton';
import type {MerchantOption} from './types';

const STATUS_OPTIONS = [
  {key: 'All statuses', value: ''},
  ...Object.values(BusinessInvoiceStatus).map((status) => ({
    key: capitalizeWords(status.replace(/_/g, ' ')),
    value: status,
  })),
];

type InvoicesFiltersProps = {
  filterCount: number;
  filtersToApply: BusinessInvoiceListParams;
  clearFilters: () => void;
  selectAppliedFilters: (
    name: string,
    value: string | Date | undefined,
  ) => void;
  handleApplyFilters: () => void;
  merchants: MerchantOption[];
  isLoadingMerchants: boolean;
  /** Called after an invoice is created so the list can refresh in place. */
  onInvoiceSaved?: () => void | Promise<void>;
};

export default function InvoicesFilters({
  filterCount,
  filtersToApply,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
  merchants,
  isLoadingMerchants,
  onInvoiceSaved,
}: InvoicesFiltersProps): React.JSX.Element {
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;
  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleApplyFilter = (): void => {
    handleApplyFilters();
    setShowFilters(false);
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center space-x-3">
          <Filter
            closeFilter={() => setShowFilters(false)}
            handleShowFilter={() => setShowFilters(!showFilters)}
            label="Filters"
            positionLeft={1}
            bodyClassName="w-full md:w-[600px]"
            data-cy="merchant-invoices-filter-toggle"
          >
            {showFilters && (
              <div className="p-4 space-y-4 max-w-4xl mx-auto w-full col-span-full">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col md:flex-row items-center gap-2">
                    {/* SearchableDropdown renders its own markup, so the hook
                        sits on a wrapper. */}
                    <div
                      className="w-full"
                      data-cy="merchant-invoices-filter-merchant"
                    >
                      <SearchableDropdown
                        label="Merchant"
                        placeholder="Any merchant"
                        value={filtersToApply.businessId ?? ''}
                        onChange={(value: string) =>
                          selectAppliedFilters('businessId', value)
                        }
                        options={merchants}
                        disabled={isLoadingMerchants}
                        className="w-full"
                      />
                    </div>
                    <Select
                      label="Status"
                      value={filtersToApply.status ?? ''}
                      dataCy="merchant-invoices-filter-status"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        // Empty string means "no status filter"; useFilters
                        // strips falsy values before the request is built.
                        selectAppliedFilters('status', e.target.value)
                      }
                      options={STATUS_OPTIONS}
                      className="w-full"
                    />
                  </div>

                  <Input
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      selectAppliedFilters('reference', e.target.value)
                    }
                    name="reference"
                    label="Reference"
                    type="text"
                    dataCy="merchant-invoices-filter-reference"
                    value={filtersToApply.reference ?? ''}
                    className="w-full isFull"
                    placeholder="e.g. 1503"
                  />

                  {/* Both dates or neither: useFilters rejects a lone date and
                      toasts, so the pair is enforced there rather than here. */}
                  <div className="flex flex-col md:flex-row gap-2">
                    <div data-cy="merchant-invoices-filter-from-date">
                      <DatePicker
                        label="From Date"
                        selectedDate={fromDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('fromDate', date)
                        }
                        placeholderText="Select Date"
                      />
                    </div>
                    <div data-cy="merchant-invoices-filter-to-date">
                      <DatePicker
                        label="To Date"
                        selectedDate={toDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('toDate', date)
                        }
                        placeholderText="Select Date"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex md:justify-end">
                  <Button
                    onClick={clearFilters}
                    colorScheme="cyan"
                    variant="ghost"
                    dataCy="merchant-invoices-filter-clear"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={handleApplyFilter}
                    colorScheme="cyan"
                    disabled={filterCount === 0}
                    className="ml-3"
                    dataCy="merchant-invoices-filter-apply"
                  >
                    Apply Filters {filterCount ? ` (${filterCount})` : ''}
                  </Button>
                </div>
              </div>
            )}
          </Filter>
        </div>

        <PermissionsProvider
          permission={AfriexAdminPermission.BusinessInvoiceManage}
        >
          <InvoiceCreateButton
            onSuccess={onInvoiceSaved}
            dataCy="merchant-invoices-create"
            formDataCy="merchant-invoices-form"
          />
        </PermissionsProvider>
      </div>
    </div>
  );
}
